// In-card confirmation for destructive actions.
//
// Replaces window.confirm, which was doing this job in four places. Beyond
// looking foreign, a native dialog blocks the whole tab, cannot show what it is
// about to do to which server, and offers exactly two words -- so the destroy
// flow was asking "OK = destroy the machine too" and relying on the reader to
// map OK/Cancel onto two different destructive outcomes.
//
// This renders in place, names the target, and puts the choice on screen as an
// actual choice.
//
// It also collects the *reason*. Ban and freeze are the two controls whose
// record is read months later by somebody who was not there, and a note typed
// at the moment of acting is the only version that exists -- reconstructing it
// afterwards from a timestamp and a username does not work. `note` makes the
// field appear; `noteRequired` stops the action until it is filled in, which is
// the point rather than an inconvenience.

import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useState } from 'react'

export type ConfirmOption = { value: string; label: string; danger?: boolean }

export const ConfirmBar = ({
    message,
    detail,
    confirmLabel,
    options,
    defaultOption,
    note,
    noteRequired,
    noteMinLength,
    notePlaceholder,
    onConfirm,
    onCancel,
    busy,
}: {
    message: string
    detail?: string
    confirmLabel: string
    /** Optional radio choices; the picked value is passed to onConfirm. */
    options?: ConfirmOption[]
    defaultOption?: string
    /** Label for a free-text reason. Omit it and no field is shown. */
    note?: string
    /** Refuse to confirm until the note has content. */
    noteRequired?: boolean
    /** With `noteRequired`: the shortest note the server accepts. The payout
     *  override needs 10 characters, and a button that lit up at one only
     *  turned a short reason into a refusal after the click (audit PAY-75). */
    noteMinLength?: number
    notePlaceholder?: string
    onConfirm: (choice?: string, note?: string) => void
    onCancel: () => void
    busy?: boolean
}) => {
    const [choice, setChoice] = useState(defaultOption ?? options?.[0]?.value)
    const [text, setText] = useState('')
    const picked = options?.find((o) => o.value === choice)

    // Trimmed, so a space bar is not a reason.
    const trimmedNote = text.trim()
    const minimum = Math.max(1, noteMinLength ?? 1)
    const noteMissing = !!note && !!noteRequired && trimmedNote.length < minimum

    return (
        <div className='flex flex-col gap-2 rounded border border-amber-400/40 bg-amber-400/5 p-2 text-left'>
            <div className='flex flex-row items-start gap-2'>
                <FontAwesomeIcon icon={faTriangleExclamation} className='text-amber-400 mt-0.5 shrink-0' />
                <div className='flex flex-col gap-0.5 min-w-0'>
                    <span className='text-sm'>{message}</span>
                    {detail && <span className='text-xs opacity-60'>{detail}</span>}
                </div>
            </div>

            {options && (
                <div className='flex flex-col gap-1 pl-6'>
                    {options.map((o) => (
                        <label key={o.value} className='flex flex-row items-center gap-2 text-xs cursor-pointer'>
                            <input
                                type='radio'
                                checked={choice === o.value}
                                onChange={() => setChoice(o.value)}
                                className='accent-violet-500'
                            />
                            <span className={o.danger ? 'text-red-400' : ''}>{o.label}</span>
                        </label>
                    ))}
                </div>
            )}

            {note && (
                <label className='flex flex-col gap-1 pl-6 text-xs'>
                    <span className='opacity-50 uppercase tracking-wide'>
                        {note}
                        {noteRequired && <span className='text-amber-400'> *</span>}
                        {noteRequired && minimum > 1 && (
                            <span className='normal-case tracking-normal'>
                                {' '}
                                ({trimmedNote.length}/{minimum} characters minimum)
                            </span>
                        )}
                    </span>
                    <textarea
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        rows={2}
                        autoFocus
                        placeholder={notePlaceholder}
                        // 255 because that is what Ban.ban_comment and
                        // FundsFreeze.reason hold. Truncating server-side would
                        // silently lose the end of a long explanation.
                        maxLength={255}
                        className='input-team-tools resize-y'
                    />
                </label>
            )}

            <div className='flex flex-row justify-end gap-2'>
                <button
                    onClick={onCancel}
                    disabled={busy}
                    className='rounded border border-white/20 px-3 py-1 text-xs uppercase tracking-wide hover:bg-white/10 disabled:opacity-40'
                >
                    Cancel
                </button>
                <button
                    onClick={() => onConfirm(choice, trimmedNote || undefined)}
                    disabled={busy || noteMissing}
                    title={
                        noteMissing
                            ? minimum > 1
                                ? `Say why in at least ${minimum} characters before confirming`
                                : 'Say why before confirming'
                            : undefined
                    }
                    className={`rounded px-3 py-1 text-xs font-bold uppercase tracking-wide disabled:opacity-40 ${
                        picked?.danger ?? true
                            ? 'bg-red-500/80 hover:bg-red-500'
                            : 'bg-amber-500/80 hover:bg-amber-500'
                    }`}
                >
                    {busy ? 'working…' : confirmLabel}
                </button>
            </div>
        </div>
    )
}
